import {
  BOM_TYPEAHEAD_MIN_WIDTH_PX,
  BOM_TYPEAHEAD_SUGGESTIONS_CONTAINER_PROPS,
} from 'elements/input/autocompletePlacement'
import BasicInput from 'elements/input/BasicInput'
import { styled, TextFieldProps, Theme, useMediaQuery } from 'opensolar-ui'
import React, { memo, useCallback, useRef, useState } from 'react'
import BasicAutocompleteSuggestionList from './BasicAutocompleteSuggestionList'

const Wrapper = styled('div')({
  borderRadius: 8,
})

const BasicAutocompleteSearchInput = <T,>({
  value,
  showSuggestion,
  fullWidth,
  placeholder,
  loading,
  renderer,
  options,
  InputProps,
  onSelect,
  onChange,
  onEnter,
  onFirstTouch,
  closeSuggestion,
  onFocus,
  disabled,
  variant = 'outlined',
  shop3,
  dropdownFooter,
  richTypeahead,
  autoFocus,
}: {
  value: string
  showSuggestion: boolean
  fullWidth?: boolean
  placeholder: string
  loading: boolean
  renderer(record: T): React.ReactNode
  options: T[]
  InputProps?: TextFieldProps['InputProps']
  onSelect?(item: T): void
  onChange?(value: string): void
  onEnter?(value: string): void
  onFirstTouch(): void
  closeSuggestion(): void
  onFocus(): void
  disabled?: boolean
  variant?: TextFieldProps['variant']
  shop3?: boolean
  dropdownFooter?: React.ReactNode
  /** BOM / Supplier Assist: wide panel, auto-start placement, multi-line rows. */
  richTypeahead?: boolean
  autoFocus?: boolean
}) => {
  const isDesktopOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'))
  const suggestionMinWidth = richTypeahead && isDesktopOrTablet ? BOM_TYPEAHEAD_MIN_WIDTH_PX : undefined
  const suggestionsContainerProps = richTypeahead ? BOM_TYPEAHEAD_SUGGESTIONS_CONTAINER_PROPS : undefined

  // This state help the dropdown to rerender
  const [suggestionWidth, setSuggestionWidth] = useState(0)
  const inputEl = useRef<HTMLInputElement>()
  const [touched, setTouched] = useState<boolean>(false)

  const handleChange = (eventOrValue: React.ChangeEvent<{ value: string }> | string) => {
    const event = eventOrValue as React.ChangeEvent<{ value: string }>
    const value = event.target ? event.target.value : (eventOrValue as string)
    onChange?.(value)
    if (!touched) {
      setTouched(true)
      onFirstTouch?.()
    }
  }

  const storeInputRef = (input: any) => {
    inputEl.current = input
    // This is a workaround to make sure the suggestion dropdown to update its width
    // Unable to set entire input element as state here due to referencing issue which won't rerender
    setSuggestionWidth(input?.clientWidth || 0)
  }

  const handleSelect = useCallback(
    (item) => {
      onSelect?.(item)
      closeSuggestion()
    },
    [onSelect, closeSuggestion]
  )

  const suggestionList = showSuggestion && inputEl.current && (
    <BasicAutocompleteSuggestionList
      width={suggestionWidth}
      minWidth={suggestionMinWidth}
      handleSelect={handleSelect}
      inputEl={inputEl.current}
      loading={loading}
      options={options}
      onClose={closeSuggestion}
      renderer={renderer}
      dropdownFooter={dropdownFooter}
      suggestionsContainerProps={suggestionsContainerProps}
    />
  )

  return shop3 ? (
    <Wrapper>
      <BasicInput
        loading={loading}
        value={value}
        placeholder={placeholder}
        fullWidth={fullWidth}
        variant={variant}
        autoFocus={autoFocus}
        InputProps={{
          inputRef: storeInputRef,
          ...InputProps,
        }}
        onChange={handleChange}
        onFocus={onFocus}
        onEnter={onEnter}
        disabled={disabled}
      />
      {suggestionList}
    </Wrapper>
  ) : (
    <>
      <BasicInput
        loading={loading}
        value={value}
        placeholder={placeholder}
        fullWidth={fullWidth}
        variant={variant}
        autoFocus={autoFocus}
        InputProps={{
          inputRef: storeInputRef,
          ...InputProps,
        }}
        onChange={handleChange}
        onFocus={onFocus}
        onEnter={onEnter}
        disabled={disabled}
      />
      {suggestionList}
    </>
  )
}

export default memo(BasicAutocompleteSearchInput)
